/**
 * IntegrationCatalogDialog — Integration Hub
 * -----------------------------------------------------------------------------
 * Catálogo de aplicativos integráveis ao Uaizy, em formato "app store":
 * sidebar de categorias com contadores + busca + grid de cards.
 *
 * Ao clicar em um app, o usuário é levado à página do fornecedor, onde
 * cria/gerencia as conexões daquele sistema (N conexões por provider).
 *
 * Preparado para escalar: novos providers aparecem aqui automaticamente,
 * bastando existir na tabela `integrations.integration_providers`.
 *
 * @version 2.1.0
 * RELEASE: 2026-08-24 v2.1.0 - Aceita `initialProviderSlug` e `initialEditConnectionId`
 *          para abrir direto no conector (ou no formulário de edição da conexão).
 * RELEASE: 2026-08-23 v2.0.0 - Refatoração visual "Editorial": cabeçalho escuro de
 *          alto contraste, sidebar de categorias mais larga (288px) com busca no
 *          topo, blocos separados, item ativo com barra lateral e rodapé de
 *          contexto. Cards do catálogo com borda definida e tipografia forte.
 * RELEASE: 2026-08-23 v1.2.0 - O conector (detalhes + formulário de conexão) é
 *          renderizado como vista interna; nenhum modal é empilhado.
 * RELEASE: 2026-08-23 v1.1.0 - Navegação do conector acontece DENTRO do modal
 *          (troca de vista catálogo -> detalhes), sem fechar o dialog.
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (catálogo por categoria).
 */

import { useEffect, useMemo, useState } from 'react';
import { Plug, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogClose, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ProviderDetailPanel } from '@/components/data/ProviderDetailPanel';
import type {
  IntegrationConnection,
  IntegrationProvider,
} from '@/hooks/integrations/useIntegrationConnections';

/** Rótulos amigáveis das categorias do catálogo */
export const PROVIDER_CATEGORY_LABELS: Record<string, string> = {
  erp: 'ERP',
  tracking: 'Plataforma de Rastreamento',
  financial: 'Financeiro',
  signature: 'Assinatura Eletrônica',
  // Providers de bureaus/consultas de crédito (ex.: Brasil Cred)
  credit: 'Análise de Crédito',
  generic: 'Genérico',
  outros: 'Outros',
};


/** Devolve o rótulo da categoria (cai para o próprio slug quando desconhecida) */
export function categoryLabel(category: string): string {
  return PROVIDER_CATEGORY_LABELS[category] ?? category;
}

interface IntegrationCatalogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providers: IntegrationProvider[];
  connections: IntegrationConnection[];
  isLoading?: boolean;
  /** Abre o modal direto no conector informado (em vez do catálogo) */
  initialProviderSlug?: string | null;
  /** Abre o formulário de edição desta conexão dentro do conector */
  initialEditConnectionId?: string | null;
}

export function IntegrationCatalogDialog({
  open,
  onOpenChange,
  providers,
  connections,
  isLoading = false,
  initialProviderSlug = null,
  initialEditConnectionId = null,
}: IntegrationCatalogDialogProps) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('all');
  /** Vista atual do modal: null = catálogo | slug = detalhes do conector */
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  /** Conexão a ser editada assim que o conector abrir (deep-link interno) */
  const [pendingEditId, setPendingEditId] = useState<string | null>(null);

  /** Sincroniza a vista inicial ao abrir e reseta ao fechar */
  useEffect(() => {
    if (open) {
      setSelectedSlug(initialProviderSlug ?? null);
      setPendingEditId(initialEditConnectionId ?? null);
    } else {
      setSelectedSlug(null);
      setPendingEditId(null);
    }
  }, [open, initialProviderSlug, initialEditConnectionId]);

  /** Quantidade de conexões configuradas por provider (badge "Conectado") */
  const connectionCountBySlug = useMemo(() => {
    const map: Record<string, number> = {};
    connections.forEach((c) => {
      map[c.provider_slug] = (map[c.provider_slug] ?? 0) + 1;
    });
    return map;
  }, [connections]);

  /** Categorias existentes com contagem de apps */
  const categories = useMemo(() => {
    const map = new Map<string, number>();
    providers.forEach((p) => map.set(p.category, (map.get(p.category) ?? 0) + 1));
    return Array.from(map.entries())
      .map(([key, count]) => ({ key, count, label: categoryLabel(key) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [providers]);

  /** Busca sem acentos e sem diferenciar maiúsculas */
  const normalize = (value: string) =>
    value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    return providers.filter((p) => {
      if (category === 'enabled' && !connectionCountBySlug[p.slug]) return false;
      if (category !== 'all' && category !== 'enabled' && p.category !== category) return false;
      if (!term) return true;
      return (
        normalize(p.name).includes(term) ||
        normalize(p.description ?? '').includes(term)
      );
    });
  }, [providers, category, search, connectionCountBySlug]);

  const enabledCount = Object.keys(connectionCountBySlug).length;

  /** Abre os detalhes do fornecedor dentro do próprio modal */
  const openProvider = (slug: string) => {
    setPendingEditId(null);
    setSelectedSlug(slug);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="max-w-[95vw] w-[95vw] h-[97vh] p-0 gap-0 overflow-hidden flex flex-col"
      >
        {/* ─────────── Cabeçalho editorial (faixa escura de alto contraste) ─────────── */}
        <header className="shrink-0 bg-sidebar px-5 py-4 text-sidebar-foreground sm:px-8 sm:py-5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sidebar-foreground/60">
            Dados / Conexões
          </p>
          <div className="mt-1 flex flex-wrap items-end justify-between gap-2 pr-10">
            <DialogTitle className="text-xl font-extrabold tracking-tight sm:text-2xl">
              Aplicativos e integrações
            </DialogTitle>
            <DialogDescription className="text-xs text-sidebar-foreground/60">
              {providers.length} aplicativos · {enabledCount} conectados
            </DialogDescription>
          </div>
          {/* Botão fechar customizado — visível sobre o cabeçalho escuro */}
          <DialogClose className="absolute right-4 top-4 rounded-sm text-sidebar-foreground/70 opacity-80 transition-opacity hover:text-sidebar-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-sidebar-foreground/40">
            <X className="h-5 w-5" />
            <span className="sr-only">Fechar</span>
          </DialogClose>
        </header>

        <div className="flex flex-1 min-h-0 flex-col sm:flex-row">
          {/* ─────────── Sidebar de categorias (288px, com busca e rodapé) ─────────── */}
          <aside className="flex w-full shrink-0 flex-col border-b bg-muted/30 sm:w-72 sm:border-b-0 sm:border-r">
            {/* Busca do aplicativo mora na sidebar */}
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  maxLength={80}
                  placeholder="Buscar aplicativo"
                  className="h-9 bg-background pl-8 text-sm"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <ScrollArea className="flex-1 max-h-[200px] sm:max-h-none">
              <nav className="space-y-1 px-3 pb-4">
                <CategoryItem
                  label="Habilitados"
                  count={enabledCount}
                  active={category === 'enabled'}
                  onClick={() => setCategory('enabled')}
                />
                <CategoryItem
                  label="Todos"
                  count={providers.length}
                  active={category === 'all'}
                  onClick={() => setCategory('all')}
                />

                <p className="px-3 pb-1 pt-6 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  Categorias
                </p>
                {categories.map((c) => (
                  <CategoryItem
                    key={c.key}
                    label={c.label}
                    count={c.count}
                    active={category === c.key}
                    onClick={() => setCategory(c.key)}
                  />
                ))}
              </nav>
            </ScrollArea>

            {/* Rodapé de contexto */}
            <div className="hidden border-t p-4 text-[11px] leading-relaxed text-muted-foreground sm:block">
              Cada aplicativo pode ter mais de uma conexão ativa.
            </div>
          </aside>

          {/* ─────────── Detalhes do conector (navegação interna) ───────────
              O conteúdo (incluindo o formulário de conexão) é renderizado como
              vista dentro do modal — nunca como um segundo dialog empilhado. */}
          {selectedSlug ? (
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              <ProviderDetailPanel
                providerSlug={selectedSlug}
                variant="dialog"
                initialEditConnectionId={pendingEditId}
                onInitialEditConsumed={() => setPendingEditId(null)}
                onBack={() => {
                  setPendingEditId(null);
                  setSelectedSlug(null);
                }}
              />
            </div>
          ) : (
          /* ─────────── Grid de apps ─────────── */
          <div className="flex min-w-0 flex-1 flex-col">
            <ScrollArea className="flex-1">
              <div className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3">
                {isLoading &&
                  [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}

                {!isLoading &&
                  filtered.map((provider) => {
                    const count = connectionCountBySlug[provider.slug] ?? 0;
                    return (
                      <button
                        key={provider.slug}
                        type="button"
                        onClick={() => openProvider(provider.slug)}
                        className="group flex items-start gap-3 rounded-md border border-border bg-card p-4 text-left transition-colors hover:border-foreground/40 hover:bg-muted/40"
                      >
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border bg-muted/60">
                          <Plug className="h-5 w-5 text-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-bold text-foreground">
                              {provider.name}
                            </span>
                            {count > 0 && (
                              <Badge variant="outline" className="shrink-0 text-[10px] font-bold uppercase tracking-wide">
                                {count === 1 ? 'Conectado' : `${count} conexões`}
                              </Badge>
                            )}
                          </div>
                          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                            {provider.description || categoryLabel(provider.category)}
                          </p>
                          <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                            {categoryLabel(provider.category)}
                          </p>
                        </div>
                      </button>
                    );
                  })}

                {!isLoading && filtered.length === 0 && (
                  <div className="col-span-full py-16 text-center text-sm text-muted-foreground">
                    Nenhum aplicativo encontrado.
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Item da sidebar de categorias.
 * Ativo: fundo do cartão, borda fina e barra vertical à esquerda (Modelo Editorial).
 */
function CategoryItem({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex h-9 w-full items-center justify-between rounded-md px-3 text-sm transition-colors',
        active
          ? 'border border-border bg-card font-semibold text-foreground shadow-sm'
          : 'text-foreground/80 hover:bg-muted',
      )}
    >
      {active && (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-foreground" />
      )}
      <span className={cn('truncate', active && 'pl-1')}>{label}</span>
      <span
        className={cn(
          'ml-2 text-xs tabular-nums',
          active ? 'font-bold text-foreground' : 'text-muted-foreground',
        )}
      >
        {count}
      </span>
    </button>
  );
}
